/**
 * Auto-highlight overlay (Part VIII): every failed/flagged finding with a page_ref
 * gets a highlight region on that page. Where the finding's evidence_snippet can be
 * located in the page text, a tight box is drawn around it; otherwise the fallback
 * is a deliberate full-page-edge treatment (inset border + a small status tab per
 * finding) - clearly "somewhere on this page", never a false-precise box.
 *
 * Visuals reuse the existing status-mark language (DESIGN.md): muted red for fail,
 * muted amber for flag, applied to the marks only. Pointer events stay off so the
 * markup tools underneath keep working; the tabs are the one interactive spot
 * (title tooltip only).
 */
import { useEffect, useState } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { locateSnippet, type NormalisedRect } from '../../lib/evidenceLocator';

/** One failed/flagged finding to visualise, already resolved to a document + page. */
export interface HighlightFinding {
  id: string;
  page: number;
  tone: 'fail' | 'flag';
  label: string;
  snippet: string | null;
}

const TONE_COLOR: Record<'fail' | 'flag', string> = {
  fail: '#C0392B', // --fail
  flag: '#B7791F', // --flag
};
const TONE_GLYPH: Record<'fail' | 'flag', string> = { fail: '✗', flag: '!' };

interface ComplianceHighlightLayerProps {
  page: number;
  doc: PDFDocumentProxy | null;
  findings: HighlightFinding[];
}

type Located = Record<string, NormalisedRect | null>;

export function ComplianceHighlightLayer({ page, doc, findings }: ComplianceHighlightLayerProps) {
  const pageFindings = findings.filter((f) => f.page === page);
  const [located, setLocated] = useState<Located>({});

  useEffect(() => {
    if (!doc || pageFindings.length === 0) return;
    let cancelled = false;
    void Promise.all(
      pageFindings.map(async (f) => [f.id, f.snippet ? await locateSnippet(doc, page, f.snippet) : null] as const),
    ).then((entries) => {
      if (!cancelled) setLocated(Object.fromEntries(entries));
    });
    return () => {
      cancelled = true;
    };
    // pageFindings is derived from findings+page; keying on those keeps the deps honest.
  }, [doc, page, findings]);

  if (pageFindings.length === 0) return null;

  const boxed = pageFindings.filter((f) => located[f.id]);
  const edge = pageFindings.filter((f) => !located[f.id]);
  const edgeTone: 'fail' | 'flag' = edge.some((f) => f.tone === 'fail') ? 'fail' : 'flag';

  return (
    <svg
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
      className="markup-layer"
      // Absolutely positioned so it stacks under the (normally-flowed) MarkupLayer
      // inside the same .pdfviewer__overlay box.
      style={{ pointerEvents: 'none', position: 'absolute', inset: 0 }}
    >
      {/* Snippet-located findings: a tight, translucent box around the evidence. */}
      {boxed.map((f) => {
        const r = located[f.id]!;
        return (
          <g key={f.id}>
            <title>{`${f.tone === 'fail' ? 'Fail' : 'Flag'}: ${f.label}`}</title>
            <rect
              x={r.x}
              y={r.y}
              width={r.w}
              height={r.h}
              fill={TONE_COLOR[f.tone]}
              fillOpacity={0.16}
              stroke={TONE_COLOR[f.tone]}
              strokeOpacity={0.85}
              strokeWidth={0.0025}
              rx={0.004}
            />
          </g>
        );
      })}

      {/* Unlocated findings: full-page-edge treatment - one inset border plus a stacked tab per finding. */}
      {edge.length > 0 ? (
        <rect
          x={0.006}
          y={0.006}
          width={0.988}
          height={0.988}
          fill="none"
          stroke={TONE_COLOR[edgeTone]}
          strokeOpacity={0.55}
          strokeWidth={0.004}
          rx={0.006}
        />
      ) : null}
      {edge.map((f, i) => {
        const y = 0.06 + i * 0.075;
        if (y > 0.94) return null; // never draw off the page; the findings list still carries everything
        return (
          <g key={f.id} style={{ pointerEvents: 'auto' }}>
            <title>{`${f.tone === 'fail' ? 'Fail' : 'Flag'}: ${f.label} — somewhere on this page (no exact position found)`}</title>
            <rect x={0} y={y} width={0.035} height={0.05} fill={TONE_COLOR[f.tone]} fillOpacity={0.92} rx={0.004} />
            <text
              x={0.0175}
              y={y + 0.028}
              fontSize={0.026}
              fill="#fff"
              textAnchor="middle"
              dominantBaseline="middle"
              style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 700, userSelect: 'none' }}
            >
              {TONE_GLYPH[f.tone]}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
